import { ControlPanelMenuItem, NavBarProps } from '@opengov/bnp-component-navbar';
import { IAdminBarProps, IEntitySwitcherProps, INavigationBarProps } from '@opengov/components-navigation';
import { getCurrentPathname } from '@opengov/compose-app-router/selectors';
import { List } from 'immutable';
import { createSelector, createStructuredSelector } from '@opengov/compose-app/reselect';
import { selectFeatureFlag } from '@opengov/feature-flag/selectors';
import { assign, template } from 'lodash';
import { ReportingConfig } from '@opengov/app-config-reporting';

import { entityEffects } from '../entity';
import { entityActionCreators } from '../entity/actionsAndReducer';
import { Entity } from '../entity/records';
import {
  entityFeaturePermissionsSelector,
  getEntity,
  getEntityId,
  getEntityName,
  getSelectableEntities,
  isEntitySelectedSelector,
  isEntitySwitcherOpen
} from '../entity/selectors';
import {
  getSession,
  getSessionEnvironment,
  getSessionRoleName,
  isOpenGovRole,
  menuItemsSelector,
  sessionFeaturePermissionSelector
} from '../session/selectors';
import { IControlPanelMenuItem } from '../types';

export const isLoading = (state): boolean => !(getEntity(state) && getSession(state));

const returnPathName = (path) => {
  const index = path.indexOf('/', 1);
  return index > 0 ? path.slice(0, index) : path;
};

export const returnFeatureFlagName = (path): string => {
  const pathName = returnPathName(path).slice(1);
  switch (pathName) {
    case 'budgets':
    case 'strategic-initiatives':
    case 'stories':
      return 'product-budget-feature-app-switcher';
    case 'reporting':
      return 'product-reporting-feature-app-switcher';
    default:
      return 'product-reporting-feature-app-switcher';
  }
};

export const getAppSwitcherFeatureFlag = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  Boolean(getFeatureFlag(returnFeatureFlagName(window.location.pathname)))
);

export const getCurrentAppPathname = createSelector(getCurrentPathname, returnPathName);

export const selectActiveProduct = createSelector(getCurrentPathname, (path) => {
  const pathname = returnPathName(path).slice(1);
  switch (pathname) {
    case 'budgets':
      return 'budget';
    case 'reporting':
      return 'reporting';
    default:
      return 'reporting';
  }
});

export const appFeaturePermissionSelector = (state) => {
  return isEntitySelectedSelector(state)
    ? entityFeaturePermissionsSelector(state)
    : sessionFeaturePermissionSelector(state);
};

type EntityRecord = InstanceType<typeof Entity>;

const PSP_MENU_ENTRYPOINTS: Record<string, (entity: EntityRecord) => string> = {
  data: (entity) => `/data_manager/${entity.subdomain}`
};

function getControlPanelMenuItemEntrypoint(
  menuItem: { id: string; location_template: string },
  entity: EntityRecord
): string {
  const getPspEntrypoint = ReportingConfig.IS_PSP_BUILD && PSP_MENU_ENTRYPOINTS[menuItem.id];

  if (getPspEntrypoint && entity.subdomain) {
    return getPspEntrypoint(entity);
  }

  return template(menuItem.location_template)({ entity: entity.id });
}

export const appSelector = createSelector(
  isEntitySelectedSelector,
  menuItemsSelector,
  getEntity,
  appFeaturePermissionSelector,
  (isEntitySelected, menuItems, entity = Entity(), featurePermissions = []) => {
    if (!menuItems) {
      return List<IControlPanelMenuItem>();
    }

    return menuItems
      .filter((mi) => {
        const requiredFeature = mi.requires_feature_permission;
        const requiredEntity = mi.requires_entity;

        return !(
          (requiredFeature && requiredEntity && !(featurePermissions.includes(requiredFeature) && isEntitySelected)) ||
          (requiredFeature && !featurePermissions.includes(requiredFeature)) ||
          (requiredEntity && !isEntitySelected)
        );
      })
      .map((mi): IControlPanelMenuItem => {
        return assign({}, mi, {
          entrypoint: getControlPanelMenuItemEntrypoint(mi, entity)
        });
      });
  }
);

export const selectNavigationBarProps = createStructuredSelector<any, INavigationBarProps>({
  currentEntity: getEntityId,
  currentAppPathname: getCurrentPathname,
  menuItems: appSelector,
  session: getSession,
  showAppSwitcher: getAppSwitcherFeatureFlag,
  activeProduct: selectActiveProduct
});

export const selectShowGlobalNavBar = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  getFeatureFlag('product-reporting-feature-global-nav-bar')
);

export const selectHideClassicReporting = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  getFeatureFlag('product-reporting-hide-classic')
);

export const shouldEnableGlobalNavRebranding = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  getFeatureFlag('product-budget-global-nav-rebranding')
);

export const selectIsFormAndWorkflowsFeatureEnabled = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  getFeatureFlag('product-budget-feature-forms-and-workflows')
);
export const selectIsPerformanceManagementFeatureEnabled = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  getFeatureFlag('product-budget-performance-management')
);
export const selectIsCoAMigrationEnabled = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  getFeatureFlag('product-budget-coa-migration-enabled')
);

export const selectShouldShowCoABanner = createSelector(
  getSessionRoleName,
  selectIsCoAMigrationEnabled,
  (roleName, isCoAMigrationEnabled) =>
    (roleName === 'opengov_admin' || roleName === 'reporting_super_admin') && isCoAMigrationEnabled
);

export function handleSignOut() {
  window.location.href = '/access/logout';
}

export const REPORTS_APP_KEY = 'reports';
export const REPORTS_PSP_APP_KEY = 'reportsPublicServicePlatform';
function getAppKey() {
  return ReportingConfig.IS_PSP_BUILD ? REPORTS_PSP_APP_KEY : REPORTS_APP_KEY;
}

export const selectGlobalNavBarProps = createSelector(
  selectHideClassicReporting,
  getSession,
  appSelector,
  selectIsFormAndWorkflowsFeatureEnabled,
  selectIsPerformanceManagementFeatureEnabled,
  getEntity,
  (
    hideClassicReporting,
    session,
    apps,
    isFormAndWorkflowsFeatureEnabled,
    isPerformanceManagementFeatureEnabled,
    entity = Entity()
  ): NavBarProps => {
    const auth0Config = getAuth0Config({
      clientId: ReportingConfig.AUTH0_CLIENT_ID,
      domain: ReportingConfig.AUTH0_DOMAIN,
      callbackUrl: `${window.location.origin}/auth/users/callback`
    });

    return {
      activeAppKey: getAppKey(),
      controlPanelMenuItems: apps.toJS() as ControlPanelMenuItem[],
      entitySubdomain: entity.subdomain,
      entityUuid: entity.uuid,
      entityFeaturePermissions: entity.feature_permissions,
      hideClassicReporting,
      isFormAndWorkflowsFeatureEnabled,
      isPerformanceManagementFeatureEnabled,
      ogAssistSearchProps: {
        entityFeaturePermissions: entity.feature_permissions,
        entityId: entity.entity_id,
        subdomain: entity.subdomain,
        userUuid: session.uuid
      },
      agentsBaseUrl: ReportingConfig.OG_AGENTS_BASE_URL,
      user: {
        id: session.id,
        first_name: session.first_name,
        last_name: session.last_name,
        avatar: session.avatar,
        uuid: session.uuid,
        role: session.role.name
      },
      contextSwitcherOptions: {
        baseUrl: '',
        userUUID: session.uuid,
        activeEntityId: entity.id,
        activeEntityIdType: 'BY_FRIENDLY_ID',
        auth0Config
      },
      handleSignOut
    };
  }
);

export const selectToggleEntitySwitcher = createSelector(isEntitySwitcherOpen, (isOpen) => async () => {
  entityActionCreators.toggleEntitySelectPanel();
  if (!isOpen) {
    return entityEffects.fetchEntities(null);
  }
});

export const selectAdminBarProps = createStructuredSelector<any, IAdminBarProps>({
  entityName: getEntityName,
  environmentName: getSessionEnvironment,
  showEnvironment: isOpenGovRole,
  onSwitchEntity: selectToggleEntitySwitcher
});

export const selectEntitySwitcherProps = createStructuredSelector<any, IEntitySwitcherProps>({
  currentAppPathname: getCurrentAppPathname,
  entities: getSelectableEntities,
  onClose: selectToggleEntitySwitcher
});

export function getAuth0Config({
  clientId,
  domain,
  callbackUrl
}: {
  clientId?: string;
  domain?: string;
  callbackUrl?: string;
}) {
  return clientId && domain
    ? {
      auth0domain: domain,
      auth0clientID: clientId,
      auth0callbackUrl: callbackUrl ?? `${window.location.origin}/auth/users/callback`,
      onAuth0SessionExpired: handleSignOut
    }
    : null;
}
